import { documentMaskUtils } from 'components/SignUp/data'

export function getFormattedValue(value?: string, mask: string = documentMaskUtils.mask, placeholder: string = '_') {
  if (!value) return ''

  let output = ''
  let inputIndex = 0

  for (let i = 0; i < mask.length; i += 1) {
    const currentChar = mask[i]

    if (!value.length) {
      output += ''
      break
    }
    if (currentChar === placeholder) {
      if (inputIndex < value.length) {
        output += value[inputIndex]
        inputIndex += 1
      } else {
        break
      }
    } else if (output.replace(/[^\d]/g, '') === value) {
      break
    } else {
      output += currentChar
    }
  }

  return output
}

const PAP_DATA_STORAGE_KEY = 'papData'
const PAP_DATA_TTL_MS = 1000 * 60 * 60 * 24 * 7

type TPapData = Record<string, string | number>

interface IStoredPapData {
  value: TPapData
  expiresAt: number
}

interface IGooglePopupMessage {
  success: boolean
  data?: any
  error?: string
}

const buildAllowedMessageOrigins = (authUrl: string) => {
  const url = new URL(authUrl)
  const defaultOrigin = url.origin

  return [defaultOrigin]
}

export const capturePapDataFromUrl = () => {
  const params = new URLSearchParams(window.location.search)
  const papData: TPapData = {}

  const refId = params.get('ref_id')
  const coupon = params.get('coupon')
  const clickId = params.get('click_id')
  const pid = params.get('pid')

  if (refId) papData.ref_id = refId
  if (coupon) papData.coupon = coupon
  if (clickId) papData.click_id = clickId
  if (pid && !Number.isNaN(Number(pid))) papData.pid = Number(pid)

  if (!Object.keys(papData).length) return

  const payload: IStoredPapData = {
    value: papData,
    expiresAt: Date.now() + PAP_DATA_TTL_MS,
  }
  localStorage.setItem(PAP_DATA_STORAGE_KEY, JSON.stringify(payload))
}

export const getSavedPapData = (): TPapData | null => {
  const rawValue = localStorage.getItem(PAP_DATA_STORAGE_KEY)
  if (!rawValue) return null

  try {
    const parsed: IStoredPapData = JSON.parse(rawValue)
    if (!parsed?.value || !parsed?.expiresAt || parsed.expiresAt < Date.now()) {
      localStorage.removeItem(PAP_DATA_STORAGE_KEY)
      return null
    }

    return parsed.value
  } catch (e) {
    localStorage.removeItem(PAP_DATA_STORAGE_KEY)
    return null
  }
}

export const startGooglePopupAuth = async ({
  getAuthUrl,
  getAuthResult,
  onSuccess,
}: {
  getAuthUrl: () => Promise<{ authUrl: string; requestId: string }>
  getAuthResult: (requestId: string) => Promise<{ ready: boolean; payload?: any }>
  onSuccess: (data: any) => void
}) => {
  const popupWindowName = `google-auth-popup-${Date.now()}`
  const popup = window.open('', popupWindowName, 'width=520,height=700')
  if (!popup) throw new Error('popup_blocked')

  let authUrl = ''
  let requestId = ''
  try {
    const oauthData = await getAuthUrl()
    authUrl = oauthData.authUrl
    requestId = oauthData.requestId
  } catch (e) {
    popup.close()
    throw e
  }

  if (!authUrl) {
    popup.close()
    throw new Error('google_auth_url_missing')
  }

  const absoluteAuthUrl = new URL(authUrl, window.location.origin).toString()
  const allowedMessageOrigins = buildAllowedMessageOrigins(absoluteAuthUrl)
  popup.location.replace(absoluteAuthUrl)

  await new Promise<void>((resolve, reject) => {
    let pollInterval: ReturnType<typeof setInterval>
    let timeoutId: ReturnType<typeof setTimeout>

    const cleanup = () => {
      clearInterval(pollInterval)
      clearTimeout(timeoutId)
      window.removeEventListener('message', onMessage)
      if (!popup.closed) popup.close()
    }

    function onMessage(event: MessageEvent<IGooglePopupMessage>) {
      if (!allowedMessageOrigins.includes(event.origin) || !event.data) return

      cleanup()
      if (event.data.success && event.data.data) {
        onSuccess(event.data.data)
        resolve()
      } else {
        reject(new Error(event.data.error || 'google_auth_failed'))
      }
    }

    pollInterval = setInterval(async () => {
      try {
        const authResult = await getAuthResult(requestId)
        if (!authResult.ready) return

        cleanup()
        if (authResult.payload?.success && authResult.payload?.data) {
          onSuccess(authResult.payload.data)
          resolve()
        } else {
          reject(new Error(authResult.payload?.error || 'google_auth_failed'))
        }
      } catch {
        // ignore polling errors, timeout handles completion
      }
    }, 700)

    timeoutId = setTimeout(() => {
      cleanup()
      reject(new Error('google_auth_timeout'))
    }, 120000)

    window.addEventListener('message', onMessage)
  })
}
